import { useContext, useState } from "react";
import {
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import JsonFileConventer from "../JsonFileConventer";
import toast from "react-hot-toast";
import { ContractFactory, ethers } from "ethers";
import axios from "axios";
import { useParams } from "react-router-dom";
import { AppContext } from "../../scripts/context/App";
import { connectWallet } from "../../layouts/smart-contracts";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 800,
  minHeight: "20vh",
  bgcolor: "#fff",
  borderRadius: "4px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
};

const ACTIONS_STYLES = {
  borderTop: "1px solid #d3d3d3",
  p: 3,
  display: "flex",
  justifyContent: "flex-end",
  gap: 1,
};

const namesByChainID: any = {
  365: "Test Net",
  361: "Main Net",
};

export default function NewDeploymentModal({
  data,
  setData,
  updateTableItems,
  handleClose,
}: any) {
  const { connectedAddress, setState: updateContextState } =
    useContext(AppContext);
  let { uuid: project_uuid } = useParams();
  const [files, setFiles] = useState<any>([{ blah: "blah" }]);
  const [loading, setLoading] = useState(false);
  const [deploymentTitle, setDeploymentTitle] = useState("");

  const submitModal = async () => {
    if (!connectedAddress) {
      toast.error("Please connect to a wallet");
      connectWallet(updateContextState);
      return;
    }

    setLoading(true);
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const network = await provider.getNetwork();
      const signer = provider.getSigner();

      const price = ethers.utils.formatUnits(
        await provider.getGasPrice(),
        "gwei"
      );
      const abiInterface = new ethers.utils.Interface(data.data.abi);

      const factory = new ContractFactory(
        abiInterface,
        data.data.bytecode,
        signer
      );
      const { data: getDeployTransactionData } = factory.getDeployTransaction(
        ...Object.values(data.data.values || {})
      );

      const estimateGas = await provider.estimateGas({
        data: getDeployTransactionData,
      });
      const options = {
        gasLimit: estimateGas,
        gasPrice: ethers.utils.parseUnits(price, "gwei"),
      };

      const contract = await factory.deploy(
        ...Object.values(data.data.values || {}),
        options
      );
      handleClose();
      toast.promise(
        contract.deployTransaction
          .wait()
          .then(async (deployTransaction: any) => {
            try {
              const formData = new FormData();
              formData.append("project_uuid", project_uuid as string);
              formData.append("deployment_title", deploymentTitle);
              formData.append(
                "smart_contract_address",
                deployTransaction.contractAddress
              );
              formData.append("owner_address", connectedAddress);
              formData.append("status", deployTransaction.status);
              formData.append("network_chain", namesByChainID[network.chainId]);
              Object.entries(data.data.values || {})?.forEach(([key, value]: any) => {
                if (typeof value === "object") {
                  formData.append(
                    key,
                    files?.filter((file: any) => file?.[key])[0][key]
                  );
                } else {
                  formData.append(key, value);
                }
              });

              console.log(formData);

              const { data: { data: createDeployment } } = await axios.post(
                "/deploy/create-deployment",
                formData
              );

              updateTableItems(createDeployment);
            } catch (error: any) {
              toast.error(
                error?.response?.data?.message ||
                error?.message ||
                error?.toString() ||
                "Error "
              );
            }
          })
          .catch((error: any) => {
            console.log(error);
          }),
        {
          loading: "Deploying...",
          success: "Deployed!",
          error: "Error",
        }
      );

      setLoading(false);
    } catch (error: any) {
      handleClose();
      console.log(error, " while submitting modal");

      toast.error(
        error.response?.data?.message ||
        "Error while deploying smart contract. Please check the console for more details."
      );
      setLoading(false);
    }
  };

  const handleChanges = (event: any) => {
    const { name, value } = event.target;

    setData({
      ...data,
      data: { ...data.data, values: { ...data.data.values, [name]: value } },
    });
  };

  if (!data.status) return <></>;
  return (
    <Modal
      keepMounted
      open={data.status}
      onClose={handleClose}
      aria-labelledby="new deployment modal"
    >
      <Box sx={style}>
        <Box sx={{ p: 3, borderBottom: "1px solid #d3d3d3" }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{ wordBreak: "break-all" }}
          >
            “{data.data.project.name}” New Deployment
          </Typography>
        </Box>

        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              id="deployment_title"
              name="deployment_title"
              label="Deployment Title"
              variant="standard"
              value={deploymentTitle}
              onChange={(event) => setDeploymentTitle(event.target.value)}
              fullWidth
            />

            <TextField
              id="network"
              name="network"
              label="Network"
              variant="standard"
              disabled
              value={"Theta Mainnet"}
              fullWidth
            />
          </Box>
          <Typography variant="body1" component="p" sx={{ fontWeight: "bold", mt: 2 }}>Initial Parameters:</Typography>
          {data.data.inputs.length > 0 ? data.data.inputs.map((input: any) => {
            if (input.type.includes("[]")) {
              return (
                <JsonFileConventer
                  key={input.name}
                  input={input}
                  isFilled={data.data.values?.[input.name]}
                  sendFile={(file: any) => {
                    setFiles([
                      ...files,
                      {
                        [input.name]: file,
                      },
                    ]);
                  }}
                  result={(value: any) => {
                    setData({
                      ...data,
                      data: {
                        ...data.data,
                        values: { ...data.data.values, [input.name]: value },
                      },
                    });
                  }}
                />
              );
            }
            return (
              <TextField
                key={input.name}
                id={input.name}
                name={input.name}
                label={input.name}
                variant="standard"
                value={data.data.values?.[input.name]}
                onChange={handleChanges}
                fullWidth
              />
            );
          }) : (
            <Typography variant="body2" component="p">
              No inputs required for this deployment.
            </Typography>
          )}
        </Box>

        <Box sx={ACTIONS_STYLES}>
          <Button
            variant="contained"
            color="primary"
            onClick={submitModal}
            disabled={loading}
            endIcon={loading && <CircularProgress size={20} />}
          >
            Create
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={loading}
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
